import { useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { Fancybox as NativeFancybox } from '@fancyapps/ui';
import hotkeys from 'hotkeys-js';
import '@fancyapps/ui/dist/fancybox/fancybox.css';
import './fancyboxOverrides.css';
import {
  ArrowClockwiseIcon,
  ArrowCounterClockwiseIcon,
  CaretDoubleDownIcon,
  CaretLeftIcon,
  CaretRightIcon,
} from '@phosphor-icons/react';

// Fancybox's arrow keys move between images, but the page also binds global
// arrow-key shortcuts (via hotkeys-js) to move between questions — both fire
// on the same keydown, so opening the zoomed view was also flipping questions
// in the background. hotkeys-js exposes exactly this kind of override via its
// `filter` hook; wrap (not replace) it once so its default input/textarea
// exclusion still applies, adding a check for whether Fancybox is open.
if (!hotkeys.filter.__wrappedForFancybox) {
  const defaultFilter = hotkeys.filter;
  const wrappedFilter = (event) => !document.querySelector('.fancybox__container') && defaultFilter(event);
  wrappedFilter.__wrappedForFancybox = true;
  hotkeys.filter = wrappedFilter;
}

const COLLAPSED_HEIGHT = 204;
const EXPANDED_MAX_HEIGHT = 484;
const SWIPE_THRESHOLD = 50;
// Movement below this is normal click/tap jitter (esp. on trackpads) and must
// NOT suppress the click-to-zoom - only count it as an intentional swipe past this.
const SWIPE_INTENT_THRESHOLD = 15;
const CAPTION = 'You can zoom in for better view';

const SolutionImage = ({
  images = [],
  expanded: controlledExpanded,
  onExpandedChange,
  collapsedHeight = COLLAPSED_HEIGHT,
}) => {
  const isControlled = controlledExpanded !== undefined;
  const [localExpanded, setLocalExpanded] = useState(false);
  const expanded = isControlled ? controlledExpanded : localExpanded;
  const setExpanded = (next) => {
    const value = typeof next === 'function' ? next(expanded) : next;
    if (isControlled) onExpandedChange?.(value);
    else setLocalExpanded(value);
  };
  const [activeIndex, setActiveIndex] = useState(0);
  const [dragOffset, setDragOffset] = useState(0);
  const [isDragging, setIsDragging] = useState(false);
  const [isZoomOpen, setIsZoomOpen] = useState(false);
  const [isTruncated, setIsTruncated] = useState(false);
  const trackRef = useRef(null);
  const dragStateRef = useRef(null);
  const wasSwipedRef = useRef(false);

  // Only show "Tap to view full answer" when the image is actually taller than
  // the collapsed box - re-check as images load async and change the height.
  useEffect(() => {
    const el = trackRef.current;
    if (!el) return;

    const checkOverflow = () => setIsTruncated(el.scrollHeight > collapsedHeight + 1);
    checkOverflow();

    const observer = new ResizeObserver(checkOverflow);
    observer.observe(el);
    return () => observer.disconnect();
  }, [images, collapsedHeight]);

  if (!images.length) return null;

  const hasMultiple = images.length > 1;

  const goToPrev = () => setActiveIndex((prev) => (prev === 0 ? images.length - 1 : prev - 1));
  const goToNext = () => setActiveIndex((prev) => (prev === images.length - 1 ? 0 : prev + 1));

  // Fancybox's own toolbar renders non-React SVG icons and is fiddly to
  // reposition reliably via CSS, so rotate is a plain React overlay instead,
  // driven through Panzoom's rotate API on the currently-shown slide.
  const openZoomedView = (startIndex) => {
    NativeFancybox.show(
      images.map((src) => ({ src, type: 'image', caption: CAPTION })),
      {
        startIndex,
        Thumbs: false,
        Toolbar: {
          display: {
            left: ['infobar'],
            middle: [],
            right: ['iterateZoom', 'slideshow', 'fullscreen', 'close'],
          },
        },
        on: {
          close: () => setIsZoomOpen(false),
        },
      },
    );
    setIsZoomOpen(true);
  };

  const rotateZoomedView = (direction) => {
    const slide = NativeFancybox.getSlide();
    if (direction === 'ccw') slide?.panzoom?.rotateCCW();
    else slide?.panzoom?.rotateCW();
  };

  // Horizontal swipe (touch drag, or mouse drag on desktop) slides between images,
  // tracking the pointer 1:1 while held and snapping to the nearest slide on release.
  const handlePointerDown = (e) => {
    if (!hasMultiple) return;
    // Stop the browser's native "drag this image" gesture from hijacking the
    // same mousedown-and-move sequence our swipe logic tracks.
    if (e.pointerType !== 'touch') e.preventDefault();
    dragStateRef.current = { startX: e.clientX, startY: e.clientY };
    wasSwipedRef.current = false;
    setIsDragging(true);
    e.currentTarget.setPointerCapture(e.pointerId);
  };

  const handlePointerMove = (e) => {
    const drag = dragStateRef.current;
    if (!drag) return;
    const deltaX = e.clientX - drag.startX;
    const deltaY = e.clientY - drag.startY;
    if (Math.abs(deltaX) > Math.abs(deltaY)) {
      e.preventDefault();
      setDragOffset(deltaX);
      if (Math.abs(deltaX) > SWIPE_INTENT_THRESHOLD) wasSwipedRef.current = true;
    }
  };

  const handlePointerUp = (e) => {
    const drag = dragStateRef.current;
    if (!drag) return;
    if (Math.abs(dragOffset) >= SWIPE_THRESHOLD) {
      if (dragOffset < 0) goToNext();
      else goToPrev();
    }
    dragStateRef.current = null;
    setDragOffset(0);
    setIsDragging(false);
    e.currentTarget.releasePointerCapture?.(e.pointerId);
  };

  // A plain click opens the zoomed view; a click that followed a drag/swipe
  // gesture shouldn't also open it.
  const handleImageClick = () => {
    if (wasSwipedRef.current) {
      wasSwipedRef.current = false;
      return;
    }
    openZoomedView(activeIndex);
  };

  return (
    <div className="flex flex-col rounded-lg overflow-hidden bg-[var(--q3-surface-dim)]">
      <div className="relative">
        <div className="overflow-hidden" style={{ maxHeight: expanded ? EXPANDED_MAX_HEIGHT : collapsedHeight }}>
          <div
            ref={trackRef}
            role="button"
            tabIndex={0}
            className={`flex ${hasMultiple ? (isDragging ? 'cursor-grabbing' : 'cursor-grab') : 'cursor-zoom-in'}`}
            style={{
              transform: `translateX(calc(-${activeIndex * 100}% + ${dragOffset}px))`,
              transition: isDragging ? 'none' : 'transform 300ms ease',
              touchAction: hasMultiple ? 'pan-y' : undefined,
            }}
            onPointerDown={hasMultiple ? handlePointerDown : undefined}
            onPointerMove={hasMultiple ? handlePointerMove : undefined}
            onPointerUp={hasMultiple ? handlePointerUp : undefined}
            onPointerCancel={hasMultiple ? handlePointerUp : undefined}
            onClick={handleImageClick}
            onKeyDown={(e) => e.key === 'Enter' && openZoomedView(activeIndex)}
          >
            {images.map((src, index) => (
              <div key={src} className="w-full flex-shrink-0">
                <img
                  src={src}
                  alt={`solution-${index + 1}`}
                  className={`w-full select-none pointer-events-none ${
                    expanded ? 'max-h-[484px] object-contain mx-auto' : 'object-cover'
                  }`}
                  style={expanded ? { maxHeight: EXPANDED_MAX_HEIGHT } : undefined}
                  draggable={false}
                />
              </div>
            ))}
          </div>
        </div>

        {isTruncated && (
          <button
            type="button"
            onClick={() => openZoomedView(activeIndex)}
            className="cursor-pointer absolute left-1/2 -translate-x-1/2 bottom-3 flex items-center gap-1 bg-white text-black text-[10px] md:text-xs font-medium p-1 px-1.5 md:px-3 md:py-1.5 rounded-full shadow"
          >
            Tap to view full answer
            <CaretDoubleDownIcon size={14} />
          </button>
        )}
      </div>

      {hasMultiple && (
        <div className="flex items-center justify-end gap-2 px-3 py-2">
          <button
            type="button"
            onClick={goToPrev}
            className="cursor-pointer flex items-center justify-center h-6 w-6 rounded-full text-[var(--q3-neutral-default)]"
          >
            <CaretLeftIcon size={14} />
          </button>
          <span className="text-xs font-medium text-[var(--q3-neutral-default)] tabular-nums">
            {activeIndex + 1}/{images.length}
          </span>
          <button
            type="button"
            onClick={goToNext}
            className="cursor-pointer flex items-center justify-center h-6 w-6 rounded-full text-[var(--q3-neutral-default)]"
          >
            <CaretRightIcon size={14} />
          </button>
        </div>
      )}

      {isZoomOpen &&
        createPortal(
          <div className="fixed left-1/2 bottom-16 -translate-x-1/2 flex items-center gap-3" style={{ zIndex: 1060 }}>
            <button
              type="button"
              onClick={() => rotateZoomedView('ccw')}
              className="flex items-center justify-center h-11 w-11 rounded-full bg-[var(--q3-surface-default)] text-[var(--q3-neutral-default)] shadow-md hover:bg-neutral-100 cursor-pointer"
            >
              <ArrowCounterClockwiseIcon size={20} />
            </button>
            <button
              type="button"
              onClick={() => rotateZoomedView('cw')}
              className="flex items-center justify-center h-11 w-11 rounded-full bg-[var(--q3-surface-default)] text-[var(--q3-neutral-default)] shadow-md hover:bg-neutral-100 cursor-pointer"
            >
              <ArrowClockwiseIcon size={20} />
            </button>
          </div>,
          document.body,
        )}
    </div>
  );
};

export default SolutionImage;
